import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import {
	Button,
  Tabs,
  Tab,
  LinearProgress,
  Tooltip,
} from '@material-ui/core';
import { EmojiEvents, SportsKabaddi, Schedule, Dns } from '@material-ui/icons';
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { DuelFeedData, DuelFeedQuery, DuelFeedVars } from '@pvplevelup/core/dist/queries/duel/duel';
import { IDuel, IDuelPerformance } from '@pvplevelup/core/dist/interfaces/IDuel';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { classImages } from 'utilities/ClassImages';
import { PageHeader, PageEmpty, PAGE_KIT, usePageStyles } from 'components/Page/PageKit';
import { formatDuration } from './DuelRoundBreakdown';

const WIN_COLOR = '#66bb6a';
const LOSS_COLOR = '#ef5350';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles((theme) => ({
  list: {
    padding: theme.spacing(1.25),
  },
  card: {
    display: 'grid',
    gridTemplateColumns: '150px minmax(420px, 1fr) 120px',
    gridGap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(1.25, 2, 1.25, 1.75),
    borderRadius: 12,
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    background: 'rgba(255, 255, 255, 0.025)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    transition: 'background 0.15s, border-color 0.15s',
    '& + &': {
      marginTop: theme.spacing(1),
    },
    '&:hover': {
      background: 'rgba(66, 165, 245, 0.06)',
      borderColor: 'rgba(66, 165, 245, 0.35)',
    },
  },
  /** ranked duels get an accent down the left edge */
  edge: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
  },
  date: {
    fontSize: '0.92rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  pills: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    gridGap: 6,
    marginTop: 6,
  },
  versus: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    alignItems: 'center',
    gridGap: theme.spacing(2),
  },
  player: {
    display: 'grid',
    gridGap: 10,
    alignItems: 'center',
    minWidth: 0,
  },
  classTile: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'grid',
    placeItems: 'center',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    '& img': {
      width: 28,
      height: 28,
    },
  },
  name: {
    fontSize: '0.95rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  playerMeta: {
    marginTop: 1,
    fontSize: '0.75rem',
    color: PAGE_KIT.muted,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  score: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: 8,
    alignItems: 'center',
    padding: '4px 14px',
    borderRadius: 10,
    background: 'rgba(0, 0, 0, 0.25)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    fontWeight: 800,
    fontSize: '1.35rem',
    lineHeight: 1.2,
    letterSpacing: '0.04em',
  },
  trophy: {
    fontSize: 15,
    color: '#f9d976',
    verticalAlign: -2,
    marginLeft: 4,
    marginRight: 4,
  },
  side: {
    textAlign: 'right',
    fontSize: '0.8rem',
    color: PAGE_KIT.muted,
    whiteSpace: 'nowrap',
    '& svg': {
      fontSize: 14,
      marginRight: 4,
      verticalAlign: -2,
      color: PAGE_KIT.faint,
    },
  },
  fightTime: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#fff',
  },
}));

/** "All" is the default and shows ranked + unranked duels. */
enum DuelFilter {
  All = "All",
  Ranked = "Ranked",
  Unranked = "Unranked",
}

const filterToRanked = (filter: DuelFilter) => filter == DuelFilter.All ? null : filter == DuelFilter.Ranked;

const MmrChange: React.FC<{value?: number | null}> = ({value}) => {
  if(value == null)
    return null;
  let color = value > 0 ? WIN_COLOR : value < 0 ? LOSS_COLOR : '#bdbdbd';
  return <span style={{color, fontWeight: 700}}>{value > 0 ? '+' : ''}{value} MMR</span>;
}

/** One side of the duel; the left one reads right to left so the names meet in the middle. */
const PlayerCell: React.FC<{performance?: IDuelPerformance, winner: boolean, align: 'left' | 'right'}> = ({performance, winner, align}) => {
  const classes = useStyles();
  if(!performance)
    return <span />;
  const right = align == 'right';
  const icon = (
    <div className={classes.classTile} style={winner ? {borderColor: 'rgba(102, 187, 106, 0.55)', boxShadow: '0 0 12px rgba(102, 187, 106, 0.2)'} : undefined}>
      <img src={classImages.getColorIcon(performance.class as AocClasses)} title={performance.class} />
    </div>
  );
  const text = (
    <div style={{minWidth: 0, textAlign: right ? 'right' : 'left'}}>
      <div className={classes.name} style={{color: winner ? WIN_COLOR : undefined}}>
        {right && winner && <EmojiEvents className={classes.trophy} titleAccess="Winner" />}
        {performance.name}
        {!right && winner && <EmojiEvents className={classes.trophy} titleAccess="Winner" />}
      </div>
      <div className={classes.playerMeta}>
        {performance.class}{performance.mmrChange != null ? ' · ' : ''}<MmrChange value={performance.mmrChange} />
      </div>
    </div>
  );
  return (
    <div className={classes.player} style={{gridTemplateColumns: right ? 'minmax(0, 1fr) auto' : 'auto minmax(0, 1fr)'}}>
      {right ? <React.Fragment>{text}{icon}</React.Fragment> : <React.Fragment>{icon}{text}</React.Fragment>}
    </div>
  );
}

export const DuelList: React.FC = () => {
  const history = useHistory();
  const classes = useStyles();
  const page = usePageStyles();
  const [filter, setFilter] = useState<DuelFilter>(DuelFilter.All);

  const { data, loading, fetchMore } = useQuery<DuelFeedData, DuelFeedVars>(DuelFeedQuery, {
    variables: {cursor: null, search: "", ranked: filterToRanked(filter)},
    fetchPolicy: 'cache-and-network',
  });

  const duels = data?.duelFeed?.duels || [];

  const loadMore = () => {
    if(!data?.duelFeed?.hasNext || loading)
      return;
    fetchMore({
      variables: {cursor: data.duelFeed.next, search: "", ranked: filterToRanked(filter)},
      updateQuery: (prev, {fetchMoreResult}) => {
        if (!fetchMoreResult) return prev;
        return Object.assign({}, prev, {
          duelFeed: {
            ...fetchMoreResult.duelFeed,
            duels: _.uniqBy([...prev.duelFeed.duels, ...fetchMoreResult.duelFeed.duels], (duel)=>duel._id?.toString()),
          }
        });
      }
    });
  }

  const isWinner = (duel: IDuel, performance?: IDuelPerformance) =>
    duel.winner != null && performance != null && (performance.character as any)?._id == duel.winner;

	return (
		<Grid container spacing={2} className="content-container">
			<Grid item xs={12}>
        <PageHeader title="Past Duels" subtitle="Completed 1v1 duels, newest first. Open one for the round by round breakdown." icon={<SportsKabaddi />}>
          <Tabs value={filter} onChange={(event, value) => setFilter(value)} indicatorColor="primary" textColor="primary">
            <Tab value={DuelFilter.All} label="All" />
            <Tab value={DuelFilter.Ranked} label="Ranked" />
            <Tab value={DuelFilter.Unranked} label="Unranked" />
          </Tabs>
        </PageHeader>
      </Grid>
			<Grid item xs={12}>
        <div className={page.tablePanel} style={{minHeight: loading && duels.length == 0 ? 120 : undefined}}>
          {loading &&<LinearProgress style={{position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 2}} />}
          {duels.length > 0 &&
            <div className={classes.list} style={{overflowX: 'auto'}}>
              {duels.map((duel) => {
                let [one, two] = duel.performances || [];
                const when = moment(duel.start_time || duel.createdAt);
                const oneWon = isWinner(duel, one);
                const twoWon = isWinner(duel, two);
                const oneRounds = one?.roundsWon || 0;
                const twoRounds = two?.roundsWon || 0;
                // the winner, or while undecided whoever is ahead, gets the colour; the other side is dimmed
                const leadColor = (mine: number, theirs: number, won: boolean) => won ? WIN_COLOR : mine > theirs ? '#fff' : PAGE_KIT.faint;
                return (
                  <div key={duel._id?.toString()} className={classes.card} onClick={() => history.push(`/duel/${duel._id}`)}>
                    <span className={classes.edge} style={{background: duel.ranked ? PAGE_KIT.accent : 'rgba(255, 255, 255, 0.12)'}} />
                    <div style={{minWidth: 0}}>
                      <Tooltip title={when.format('MMM Do YYYY, h:mm a')}>
                        <div>
                          <div className={classes.date}>{when.format('MMM D, YYYY')}</div>
                          <div className={page.muted}>{when.fromNow()}</div>
                        </div>
                      </Tooltip>
                      <div className={classes.pills}>
                        <span className={page.pill} style={duel.ranked ? {color: '#90caf9', background: 'rgba(66, 165, 245, 0.16)'} : undefined}>{duel.ranked ? 'Ranked' : 'Unranked'}</span>
                        <span className={page.pill}>Bo{duel.bestOf || 1}</span>
                      </div>
                    </div>
                    <div className={classes.versus}>
                      <PlayerCell performance={one} winner={oneWon} align="right" />
                      <span className={classes.score}>
                        <span style={{color: leadColor(oneRounds, twoRounds, oneWon)}}>{oneRounds}</span>
                        <span style={{opacity: 0.35}}>:</span>
                        <span style={{color: leadColor(twoRounds, oneRounds, twoWon)}}>{twoRounds}</span>
                      </span>
                      <PlayerCell performance={two} winner={twoWon} align="left" />
                    </div>
                    <div className={classes.side}>
                      <Tooltip title="Fight time">
                        <div className={classes.fightTime}><Schedule />{formatDuration(duel.timeElapsed)}</div>
                      </Tooltip>
                      {duel.server && <div style={{marginTop: 4}}><Dns />{duel.server}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          }
          {!loading && duels.length == 0 &&
            <PageEmpty
              icon={<SportsKabaddi />}
              title={`No ${filter == DuelFilter.All ? '' : `${filter.toLowerCase()} `}duels yet`}
              text="Finished duels show up here with their score, fight time and MMR change."
            />
          }
          {data?.duelFeed?.hasNext &&
            <Button fullWidth className={page.loadMore} disabled={loading} onClick={loadMore}>Load more duels</Button>
          }
        </div>
			</Grid>
		</Grid>
	);
};
